import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import storeClient from '../api/storeClient';

const CartContext = createContext();
const STORAGE_KEY = 'ticket-cart';
const CHECKOUT_TIME_KEY = 'checkout-start-time';
const LOCK_EXPIRES_AT_KEY = 'seat-locks-expires-at';

const initialCart = {
  eventId: null,
  eventName: null,
  sections: {},
  managementFeeType: null,
  managementFeeValue: null,
  managementFeeScope: null,
  customerData: {
    name: '',
    email: '',
    phone: '',
    nif: '',
    address: '',
  },
};

const hasSeatsInCart = (cart) => {
  return Object.values(cart?.sections || {}).some(
    (section) => Array.isArray(section?.seats) && section.seats.length > 0,
  );
};

const normalizeExpiration = (expiresAt) => {
  if (!expiresAt) return null;

  if (typeof expiresAt === 'number') {
    return Number.isFinite(expiresAt) ? expiresAt : null;
  }

  const parsed = Date.parse(expiresAt);
  return Number.isNaN(parsed) ? null : parsed;
};

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initialCart;
    } catch {
      return initialCart;
    }
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  const unlockSeatsOnBackend = useCallback((eventId, seats) => {
    const seatIds = seats
      .map((s) => parseInt(s.id, 10))
      .filter((id) => Number.isInteger(id) && id > 0);
    if (!eventId || seatIds.length === 0) return;
    storeClient.delete(`/store/events/${eventId}/seats/unlock`, { data: { seat_ids: seatIds } })
      .catch(() => {});
  }, []);

  const addSeat = useCallback((eventId, eventName, sectionId, sectionName, seat, tariff) => {
    const normalizedSectionId = String(sectionId);

    setSeatLocksExpiration(Date.now() + 15 * 60 * 1000);

    setCart((prev) => {
      const currentSection = prev.sections[normalizedSectionId];
      const currentSeats = currentSection?.seats || [];
      const alreadyExists = currentSeats.some((s) => String(s.id) === String(seat.id));

      if (alreadyExists) {
        return prev;
      }

      return {
        ...prev,
        eventId,
        eventName,
        sections: {
          ...prev.sections,
          [normalizedSectionId]: {
            sectionName,
            seats: [...currentSeats, seat],
            tariff,
          },
        },
      };
    });
  }, []);
  const removeSeat = useCallback((sectionId, seatId) => {
    const normalizedSectionId = String(sectionId);
    setCart((prev) => {
      const section = prev.sections?.[normalizedSectionId];
      if (!section || !Array.isArray(section.seats)) {
        return prev;
      }

      // Release the lock for this specific seat
      const removedSeat = section.seats.find((s) => String(s.id) === String(seatId));
      if (removedSeat) {
        unlockSeatsOnBackend(prev.eventId, [removedSeat]);
      }

      const nextSeats = section.seats.filter((s) => String(s.id) !== String(seatId));
      const nextSections = { ...prev.sections };

      if (nextSeats.length === 0) {
        delete nextSections[normalizedSectionId];
      } else {
        nextSections[normalizedSectionId] = {
          ...section,
          seats: nextSeats,
        };
      }

      return {
        ...prev,
        sections: nextSections,
      };
    });
  }, [unlockSeatsOnBackend]);
  const syncSectionSeats = useCallback((eventId, eventName, sectionId, sectionName, seats, tariff = null) => {
    const normalizedSectionId = String(sectionId);

    const nextSeats = Array.isArray(seats) ? seats : [];
    if (nextSeats.length > 0) {
      setSeatLocksExpiration(Date.now() + 15 * 60 * 1000);
    }

    setCart((prev) => {
      const currentSection = prev.sections[normalizedSectionId];
      const currentSeats = currentSection?.seats || [];

      // Avoid rewriting cart on each render when selection is empty.
      if (nextSeats.length === 0 && !currentSection) {
        return prev;
      }

      const currentKey = currentSeats
        .map((s) => String(s.id) + ':' + String(s.ticketTypeId ?? '') + ':' + String(s.tariffId ?? '') + ':' + String(s.price ?? ''))
        .sort()
        .join('|');
      const nextKey = nextSeats
        .map((s) => String(s.id) + ':' + String(s.ticketTypeId ?? '') + ':' + String(s.tariffId ?? '') + ':' + String(s.price ?? ''))
        .sort()
        .join('|');

      if (
        currentKey === nextKey
        && prev.eventId === eventId
        && prev.eventName === eventName
        && (currentSection?.sectionName ?? null) === (sectionName ?? null)
      ) {
        return prev;
      }

      const nextSections = { ...prev.sections };

      if (nextSeats.length === 0) {
        delete nextSections[normalizedSectionId];
      } else {
        nextSections[normalizedSectionId] = {
          sectionName,
          seats: nextSeats,
          tariff,
        };
      }

      return {
        ...prev,
        eventId,
        eventName,
        sections: nextSections,
      };
    });
  }, []);
  const updateCustomerData = useCallback((data) => {
    setCart((prev) => {
      const nextCustomerData = { ...prev.customerData, ...data };
      const changed = Object.keys(data).some(
        (key) => prev.customerData?.[key] !== nextCustomerData[key],
      );

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        customerData: nextCustomerData,
      };
    });
  }, []);

  const updateGeneralAdmissionTickets = useCallback((eventId, tickets, managementFeeType, managementFeeValue, managementFeeScope) => {
    const sectionId = 'general-admission';
    setSeatLocksExpiration(Date.now() + 15 * 60 * 1000);

    setCart((prev) => ({
      ...prev,
      eventId,
      managementFeeType,
      managementFeeValue,
      managementFeeScope,
      sections: {
        [sectionId]: {
          sectionName: 'Admisión General',
          seats: tickets,
          tariff: null,
        },
      },
    }));
  }, []);

  const clearCart = useCallback(() => {
    // Release all seat locks before wiping local state (read via ref to avoid stale closure)
    const snapshot = cartRef.current;
    if (hasSeatsInCart(snapshot)) {
      const allSeats = Object.values(snapshot.sections).flatMap((s) => s.seats);
      unlockSeatsOnBackend(snapshot.eventId, allSeats);
    }
    setCart(initialCart);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CHECKOUT_TIME_KEY);
    localStorage.removeItem(LOCK_EXPIRES_AT_KEY);
  }, [unlockSeatsOnBackend]);

  useEffect(() => {
    const checkExpiration = () => {
      if (!hasSeatsInCart(cart)) return;

      const expiresAt = getSeatLocksExpiresAt();
      if (!expiresAt) return;

      if (Date.now() >= expiresAt) {
        clearCart();
      }
    };

    checkExpiration();
    const timer = setInterval(checkExpiration, 1000);

    return () => clearInterval(timer);
  }, [cart, clearCart]);

  const getTotalSeats = useCallback(() => {
    return Object.values(cart.sections).reduce((sum, section) => sum + section.seats.length, 0);
  }, [cart]);

  return (
    <CartContext.Provider
      value={{
        cart,
        addSeat,
        removeSeat,
        syncSectionSeats,
        updateCustomerData,
        updateGeneralAdmissionTickets,
        clearCart,
        getTotalSeats,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}

export function setSeatLocksExpiration(expiresAt) {
  try {
    const normalized = normalizeExpiration(expiresAt);
    if (!normalized) return;

    localStorage.setItem(LOCK_EXPIRES_AT_KEY, String(normalized));
  } catch {
    // no-op
  }
}

export function getSeatLocksExpiresAt() {
  try {
    const saved = localStorage.getItem(LOCK_EXPIRES_AT_KEY);
    if (!saved) return null;

    const timestamp = parseInt(saved, 10);
    return Number.isFinite(timestamp) ? timestamp : null;
  } catch {
    return null;
  }
}

export function clearSeatLocksExpiration() {
  localStorage.removeItem(LOCK_EXPIRES_AT_KEY);
}

// Helper function to manage checkout timer
export function getCheckoutStartTime() {
  try {
    const saved = localStorage.getItem(CHECKOUT_TIME_KEY);
    if (!saved) {
      const now = Date.now();
      localStorage.setItem(CHECKOUT_TIME_KEY, now);
      return now;
    }
    return parseInt(saved, 10);
  } catch {
    return Date.now();
  }
}

export function clearCheckoutTimer() {
  localStorage.removeItem(CHECKOUT_TIME_KEY);
}



